import { useEffect, useState } from "react";
import { SILK_COLOURS } from "../lib/constants";

/**
 * Overlay animation shown after Confirm All Picks succeeds.
 * Respects prefers-reduced-motion → static badge instead of gallop.
 * Shows a shareable card: "N tips locked in for <date>".
 */
export default function LockedInCelebration({ open, onClose, meetingDate, groupName, user, picks, races }) {
  const [reduced, setReduced] = useState(false);
  const [showingCard, setShowingCard] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!open) { setShowingCard(false); return; }
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReduced(rm);
    if (rm) {
      // Skip gallop; show card immediately after brief pause
      const t = setTimeout(() => setShowingCard(true), 300);
      return () => clearTimeout(t);
    }
    // Sound (Web Audio thunk) — muted by default
    if (!muted) playThunk();
    const t = setTimeout(() => setShowingCard(true), 1500);
    return () => clearTimeout(t);
  }, [open, muted]);

  if (!open) return null;

  const pickByRace = new Map();
  (picks || []).forEach((p) => pickByRace.set(p.race_id, p));
  const cards = (races || [])
    .map((r) => {
      const p = pickByRace.get(r.id);
      if (!p) return null;
      const horse = r.horses.find((h) => h.horse_number === p.horse_number);
      return { race: r, pick: p, horse };
    })
    .filter(Boolean);

  const doShare = async () => {
    const text = `🐎 Locked ${cards.length} tips for ${meetingDate} in ${groupName} — HorseTips4fun`;
    if (navigator.share) {
      try { await navigator.share({ title: "HorseTips4fun", text }); } catch (_) {}
    } else {
      try { await navigator.clipboard.writeText(text); } catch (_) {}
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="locked-in-celebration">
      {/* Gallop animation */}
      {!reduced && !showingCard && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1864ab] to-[#1971c2] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="horse-gallop text-8xl select-none">🐎</div>
          </div>
          {/* Dust particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full bg-white/70 dust-particle"
                style={{ top: `${45 + i * 3}%`, left: `${10 + i * 8}%`, animationDelay: `${0.2 + i * 0.08}s` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Card */}
      {(reduced || showingCard) && (
        <>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onClose} />
          <div className="relative bg-white rounded-3xl w-[92%] max-w-md overflow-hidden shadow-2xl animate-sheet-up" data-testid="locked-in-card">
            <div className="p-6 text-white text-center" style={{ background: "linear-gradient(135deg, #1864ab 0%, #1971c2 100%)" }}>
              <div className="text-5xl mb-2">🎯</div>
              <div className="font-display font-black text-3xl leading-tight" data-testid="locked-in-count">
                {cards.length} tip{cards.length === 1 ? "" : "s"} locked in
              </div>
              <div className="text-white/85 font-semibold text-sm mt-1">
                {groupName} · {meetingDate}
              </div>
            </div>
            <div className="p-4 space-y-2 max-h-[45vh] overflow-y-auto">
              {cards.map(({ race, horse }) => (
                <div key={race.id} className="flex items-center gap-3 bg-[#f8f9fa] border border-[#e9ecef] rounded-xl p-2.5">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center text-white font-black text-sm shrink-0"
                    style={{ backgroundColor: SILK_COLOURS[((horse?.horse_number || 1) - 1) % 12] }}
                  >
                    {horse?.horse_number}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-extrabold text-sm truncate">{horse?.name}</div>
                    <div className="text-[10px] text-[#6c757d] truncate">
                      {race.track} · R{race.race_number} · @ {horse?.odds?.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-[#e9ecef] flex items-center gap-2">
              <button
                onClick={() => setMuted((m) => !m)}
                className="rounded-full px-3 py-1.5 text-[10px] font-bold border border-[#e9ecef] text-[#6c757d]"
                title="Sound"
              >
                {muted ? "🔇 Sound off" : "🔊 Sound on"}
              </button>
              <div className="flex-1" />
              <button
                onClick={doShare}
                className="bg-white text-[#1971c2] border-2 border-[#1971c2] font-bold rounded-xl px-4 py-2.5 active:scale-95 transition-transform"
                data-testid="celebration-share"
              >
                Share
              </button>
              <button
                onClick={onClose}
                className="bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold rounded-xl px-5 py-2.5 active:scale-95 transition-transform"
                data-testid="celebration-done"
              >
                Done
              </button>
            </div>
            <div className="pb-3 text-center text-[9px] font-bold uppercase tracking-widest text-[#adb5bd]">
              HorseTips4fun
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function playThunk() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(180, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch (_) {}
}
